/**
 * StatsOverview Component
 *
 * Displays the Summary & Stats tab content including conversation summary,
 * speaker breakdown chart, and therapist analysis with markdown rendering.
 *
 * IMPORTANT: This component shows data if it exists. The `summaryStatus` and
 * `analysisStatus` props are ONLY used to show progress during initial generation,
 * NOT to control visibility of existing data.
 */

import {
  Loader2,
  FileText,
  BarChart2,
  MessageSquare,
  Sparkles,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import type {
  SpeakerResponse,
  ConversationSentimentResponse,
} from "@/types/transcript";
import type { AnnotationSummaryResponse } from "@/types/annotation";
import type { ReflectionFeedbackResponse } from "@/types/reflection";
import type { AnalysisComponentStatus } from "@/hooks/useAnalysisStatus";

interface StatsOverviewProps {
  /** Speakers in the conversation */
  speakers: SpeakerResponse[];
  /** Annotation summary with conversation overview */
  summary: AnnotationSummaryResponse | null;
  /** Therapist feedback/analysis */
  feedback: ReflectionFeedbackResponse | null;
  /** Conversation-level sentiment analysis */
  sentiment?: ConversationSentimentResponse | null;
  /** Whether data is loading from API */
  isLoading?: boolean;
  /** Whether feedback/analysis is being regenerated with a new therapy style */
  isRegenerating?: boolean;
  /** Status of the summary generation (from pipeline) - only for progress display */
  summaryStatus?: AnalysisComponentStatus;
  /** Status of the analysis/feedback generation (from pipeline) - only for progress display */
  analysisStatus?: AnalysisComponentStatus;
  /** Callback to retry failed generation */
  onRetry?: () => void;
}

/**
 * Speaker colors matching the design
 */
const SPEAKER_COLORS = [
  {
    bar: "bg-cyan-500 dark:bg-cyan-600",
    avatar: "bg-cyan-500 dark:bg-cyan-600",
  },
  {
    bar: "bg-purple-500 dark:bg-purple-600",
    avatar: "bg-purple-500 dark:bg-purple-600",
  },
  {
    bar: "bg-orange-500 dark:bg-orange-600",
    avatar: "bg-orange-500 dark:bg-orange-600",
  },
  {
    bar: "bg-pink-500 dark:bg-pink-600",
    avatar: "bg-pink-500 dark:bg-pink-600",
  },
  {
    bar: "bg-teal-500 dark:bg-teal-600",
    avatar: "bg-teal-500 dark:bg-teal-600",
  },
  {
    bar: "bg-indigo-500 dark:bg-indigo-600",
    avatar: "bg-indigo-500 dark:bg-indigo-600",
  },
];

/**
 * Speaker breakdown bar chart component
 */
function SpeakerBreakdown({ speakers }: { speakers: SpeakerResponse[] }) {
  const totalTalkTime = speakers.reduce(
    (sum, s) => sum + s.total_talk_time_ms,
    0
  );

  if (speakers.length === 0 || totalTalkTime === 0) {
    return null;
  }

  return (
    <Card className="p-6">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
        Speaker Breakdown
      </h3>

      {/* Horizontal stacked bar */}
      <div className="h-3 rounded-full overflow-hidden flex mb-4">
        {speakers.map((speaker, index) => {
          const percentage = (speaker.total_talk_time_ms / totalTalkTime) * 100;
          const colors = SPEAKER_COLORS[index % SPEAKER_COLORS.length];

          return (
            <div
              key={speaker.id}
              style={{ width: `${percentage}%` }}
              className={cn(
                "first:rounded-l-full last:rounded-r-full",
                colors.bar
              )}
              title={`${speaker.custom_name || speaker.display_label}: ${Math.round(percentage)}%`}
            />
          );
        })}
      </div>

      {/* Speaker stats */}
      <div className="flex justify-around">
        {speakers.map((speaker, index) => {
          const percentage = Math.round(
            (speaker.total_talk_time_ms / totalTalkTime) * 100
          );
          const minutes = Math.round(speaker.total_talk_time_ms / 60000);
          const colors = SPEAKER_COLORS[index % SPEAKER_COLORS.length];
          const displayLetter = speaker.display_label.charAt(0).toUpperCase();

          return (
            <div key={speaker.id} className="flex items-center gap-3">
              {/* Avatar */}
              <div
                className={cn(
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white",
                  colors.avatar
                )}
              >
                {displayLetter}
              </div>

              {/* Stats */}
              <div>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-foreground">
                    {percentage}%
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {speaker.custom_name || speaker.display_label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {minutes} {minutes === 1 ? "minute" : "minutes"} spoken
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/**
 * Inline generating state component for cards - only shown when NO data exists
 */
function GeneratingState({
  title,
  message,
  progress,
}: {
  title: string;
  message: string;
  progress?: number;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <div className="relative">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
        <h3 className="font-semibold text-foreground">{title}</h3>
      </div>
      <div className="flex flex-col items-center justify-center py-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
        <p className="text-sm text-muted-foreground text-center">{message}</p>
        {progress !== undefined && progress > 0 && progress < 100 && (
          <div className="mt-3 w-full max-w-xs">
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground text-center mt-1">
              {progress}% complete
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

/**
 * Conversation summary card component
 * Shows data if it exists, generating state only if no data AND actively generating
 */
function ConversationSummaryCard({
  summary,
  status,
  isLoading,
}: {
  summary: AnnotationSummaryResponse | null;
  status?: AnalysisComponentStatus;
  isLoading?: boolean;
}) {
  // If we have data, ALWAYS show it - this is the critical fix
  if (summary?.summary_text) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">
            Conversation Summary
          </h3>
        </div>
        <div className="prose prose-sm max-w-none text-muted-foreground [&>h3]:text-foreground [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mt-4 [&>h3]:mb-2 [&>ul]:space-y-1 [&>ul]:mt-2 [&>p]:leading-relaxed">
          <ReactMarkdown>{summary.summary_text}</ReactMarkdown>
        </div>
      </Card>
    );
  }

  // No data - show generating state only if actively generating
  if (status?.isGenerating && !isLoading) {
    return (
      <GeneratingState
        title="Conversation Summary"
        message="Analyzing your conversation to create a summary..."
        progress={status.progress}
      />
    );
  }

  // No data and not generating - return null (will show empty state at parent level if needed)
  return null;
}

/**
 * Therapist analysis card component with markdown rendering
 * Shows data if it exists, generating state only if no data AND actively generating
 */
function TherapistAnalysisCard({
  feedback,
  status,
  isLoading,
}: {
  feedback: ReflectionFeedbackResponse | null;
  status?: AnalysisComponentStatus;
  isLoading?: boolean;
}) {
  // If we have data, ALWAYS show it - this is the critical fix
  if (feedback?.feedback_text) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquare className="h-5 w-5 text-purple-500" />
          <h3 className="font-semibold text-foreground">Therapist Analysis</h3>
        </div>
        <div className="prose prose-sm max-w-none text-muted-foreground [&>h3]:text-foreground [&>h3]:text-base [&>h3]:font-semibold [&>h3]:mt-4 [&>h3]:mb-2 [&>ul]:space-y-1 [&>ul]:mt-2 [&>p]:leading-relaxed">
          <ReactMarkdown>{feedback.feedback_text}</ReactMarkdown>
        </div>
      </Card>
    );
  }

  // No data - show generating state only if actively generating
  if (status?.isGenerating && !isLoading) {
    return (
      <GeneratingState
        title="Therapist Analysis"
        message="Creating personalized therapeutic insights for your conversation..."
        progress={status.progress}
      />
    );
  }

  // No data and not generating - return null
  return null;
}

/**
 * Color mapping for overall tone badge
 */
function getToneBadgeClasses(tone: string): string {
  const lower = tone.toLowerCase();
  if (
    lower.includes("constructive") ||
    lower.includes("warm") ||
    lower.includes("positive") ||
    lower.includes("supportive")
  ) {
    return "bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800";
  }
  if (
    lower.includes("tense") ||
    lower.includes("escalating") ||
    lower.includes("hostile") ||
    lower.includes("conflict")
  ) {
    return "bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800";
  }
  return "bg-amber-50 dark:bg-amber-950 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800";
}

/**
 * Emotional Overview card showing conversation-level sentiment data
 */
function EmotionalOverviewCard({
  sentiment,
  speakers,
}: {
  sentiment: ConversationSentimentResponse;
  speakers: SpeakerResponse[];
}) {
  const speakerEntries = Object.entries(sentiment.speaker_sentiments);
  const tensionCount = sentiment.tension_peaks?.length ?? 0;
  const positiveCount = sentiment.positive_moments?.length ?? 0;

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-amber-500" />
        <h3 className="font-semibold text-foreground">Emotional Overview</h3>
      </div>

      {/* Overall Tone */}
      <div className="mb-4">
        <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1.5">
          Overall Tone
        </p>
        <span
          className={cn(
            "inline-flex px-2.5 py-1 rounded-full text-xs font-medium border capitalize",
            getToneBadgeClasses(sentiment.overall_tone)
          )}
        >
          {sentiment.overall_tone}
        </span>
      </div>

      {/* Speaker Sentiments */}
      {speakerEntries.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            Speaker Sentiments
          </p>
          <div className="space-y-2">
            {speakerEntries.map(([speakerLabel, info], index) => {
              const matchedSpeaker = speakers.find(
                (s) =>
                  s.display_label === speakerLabel ||
                  s.custom_name === speakerLabel
              );
              const displayName =
                matchedSpeaker?.custom_name ||
                matchedSpeaker?.display_label ||
                speakerLabel;
              const colors = SPEAKER_COLORS[index % SPEAKER_COLORS.length];

              return (
                <div key={speakerLabel} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-foreground w-20 truncate">
                    {displayName}
                  </span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className={cn("h-full rounded-full", colors.bar)}
                      style={{
                        width: `${Math.round(info.intensity * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground w-24 text-right capitalize">
                    {info.primary_emotion}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Key Moments */}
      {(tensionCount > 0 || positiveCount > 0) && (
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            Key Moments
          </p>
          <div className="flex gap-2">
            {tensionCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800 rounded-full text-xs font-medium">
                {tensionCount} tension {tensionCount === 1 ? "peak" : "peaks"}
              </span>
            )}
            {positiveCount > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800 rounded-full text-xs font-medium">
                {positiveCount} positive{" "}
                {positiveCount === 1 ? "moment" : "moments"}
              </span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

export function StatsOverview({
  speakers,
  summary,
  feedback,
  sentiment,
  isLoading = false,
  isRegenerating = false,
  summaryStatus,
  analysisStatus,
}: StatsOverviewProps) {
  // Determine what content we have
  const hasSummary = !!summary?.summary_text;
  const hasFeedback = !!feedback?.feedback_text;
  const hasSpeakers = speakers.length > 0;
  const hasSentiment = !!sentiment;
  const hasAnyContent =
    hasSummary || hasFeedback || hasSpeakers || hasSentiment;

  // Show loading skeleton only during initial API fetch when we have no content
  if (isLoading && !hasAnyContent) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading stats...</span>
      </div>
    );
  }

  // Check if we're actively generating (SSE says generating AND no data yet)
  const isGeneratingSummary = summaryStatus?.isGenerating && !hasSummary;
  const isGeneratingAnalysis = analysisStatus?.isGenerating && !hasFeedback;
  const isAnyGenerating = isGeneratingSummary || isGeneratingAnalysis;

  // Show empty state only when not loading, not generating, and no content
  if (!hasAnyContent && !isAnyGenerating && !isLoading) {
    return (
      <div className="text-center py-12">
        <BarChart2 className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
        <p className="text-muted-foreground">
          Statistics will be available once the conversation is analyzed.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Regenerating overlay - only show when explicitly regenerating with new therapy style */}
      {isRegenerating && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-lg">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <p className="text-sm font-medium text-foreground">
            Regenerating analysis...
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Applying new therapy lens
          </p>
        </div>
      )}

      {/* Therapy Lens Badge */}
      {feedback?.therapy_style && (
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-full">
            <Sparkles className="w-3.5 h-3.5 text-purple-500" />
            <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
              {feedback.therapy_style.name}
            </span>
          </div>
        </div>
      )}

      {/* Conversation Summary - shows data if exists, or generating state if generating */}
      <ConversationSummaryCard
        summary={summary}
        status={summaryStatus}
        isLoading={isLoading}
      />

      {/* Emotional Overview - shows when sentiment data exists */}
      {sentiment && (
        <EmotionalOverviewCard sentiment={sentiment} speakers={speakers} />
      )}

      {/* Speaker Breakdown - always show if we have speakers */}
      <SpeakerBreakdown speakers={speakers} />

      {/* Therapist Analysis - shows data if exists, or generating state if generating */}
      <TherapistAnalysisCard
        feedback={feedback}
        status={analysisStatus}
        isLoading={isLoading}
      />
    </div>
  );
}
